import { type FormEvent, useState } from 'react';
import type { GroupDto, GroupMemberDto, UserPublic } from '@mondy/shared';
import { ApiError } from '@mondy/shared/api-client';
import {
  UpgradeModal,
  upgradeReasonFromApiError,
  type UpgradeReason,
} from '@/components/UpgradeModal';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useMyGroups } from '@/hooks/useMyGroups';
import { useCreateGroup } from '@/hooks/useCreateGroup';
import { useGroupInvites } from '@/hooks/useGroupInvites';
import { useInviteByEmail } from '@/hooks/useInviteByEmail';
import { useRevokeInvite } from '@/hooks/useRevokeInvite';
import { useLeaveGroup } from '@/hooks/useLeaveGroup';
import { useDeleteGroup } from '@/hooks/useDeleteGroup';
import { useGroupMembers } from '@/hooks/useGroupMembers';
import { useKickMember } from '@/hooks/useKickMember';
import { useJoinRequests } from '@/hooks/useJoinRequests';
import { useApproveJoinRequest } from '@/hooks/useApproveJoinRequest';
import { useRejectJoinRequest } from '@/hooks/useRejectJoinRequest';
import { useActiveGroup, useSetActiveGroup } from '@/state/activeGroup';
import { Spinner } from '@/components/Spinner';
import { ConfirmModal } from '@/components/ConfirmModal';
import { TierBadge } from '@/components/TierBadge';

const inputBase =
  'h-11 w-full rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-4 text-base text-[color:var(--color-fg)] outline-none focus:border-[color:var(--color-accent)]';

/**
 * "My Groups" card on the Profile page. Top-level responsibilities:
 *   - Create a new group (single-input form)
 *   - List the user's groups with an expandable detail drawer per row
 *   - Owner actions: members list, invite link, email-invite, pending invites,
 *     pending join requests (+ approve / reject), delete
 *   - Member actions: members list, leave
 */
export function GroupsCard() {
  const me = useCurrentUser();
  const query = useMyGroups();
  const groups = query.data?.groups ?? [];

  if (!me) return null;

  return (
    <section className="flex flex-col gap-3 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4">
      <header className="flex flex-col gap-0.5">
        <h2 className="text-base font-semibold">My groups</h2>
        <p className="text-xs text-[color:var(--color-fg-muted)]">
          Scope the standings and predictions to a private club of friends.
        </p>
      </header>

      <CreateGroupForm />

      {query.isLoading ? (
        <div className="py-6">
          <Spinner size="sm" />
        </div>
      ) : groups.length === 0 ? (
        <p className="py-2 text-sm text-[color:var(--color-fg-muted)]">
          You're not in any group yet. Create one above, or wait for a friend to
          invite you by email.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {groups.map((g) => (
            <GroupRow key={g.id} group={g} me={me} />
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Small red notification dot rendered next to the Profile nav link when the
 * signed-in user has any pending join-request across their owned groups.
 * Returns null when unauthed or when there's nothing to act on.
 *
 * Positioned absolutely — the caller must provide a relatively-positioned
 * wrapper (e.g. the nav link's icon span).
 */
export function PendingRequestsDot() {
  const me = useCurrentUser();
  const query = useMyGroups();
  if (!me) return null;
  const groups = query.data?.groups ?? [];
  const total = groups.reduce((sum, g) => sum + g.pendingRequestCount, 0);
  if (total <= 0) return null;
  return (
    <span
      aria-label={`${total} pending request${total === 1 ? '' : 's'}`}
      style={{
        position: 'absolute',
        top: -2,
        right: -2,
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: 'var(--color-danger)',
        border: '1.5px solid var(--bg)',
        pointerEvents: 'none',
      }}
    />
  );
}

function CreateGroupForm() {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [upgradeReason, setUpgradeReason] = useState<UpgradeReason | null>(null);
  const setActiveGroupId = useSetActiveGroup();
  const create = useCreateGroup();

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setError('Give your group a name.');
      return;
    }
    if (trimmed.length > 64) {
      setError('Name must be 64 characters or fewer.');
      return;
    }
    try {
      const res = await create.mutateAsync(trimmed);
      setName('');
      setActiveGroupId(res.group.id);
    } catch (err) {
      const upgrade = upgradeReasonFromApiError(err);
      if (upgrade) {
        setUpgradeReason(upgrade);
        return;
      }
      setError(err instanceof ApiError ? err.message : 'Could not create group.');
    }
  }

  return (
    <>
      <form onSubmit={onSubmit} className="flex flex-col gap-2">
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="New group name"
            maxLength={64}
            disabled={create.isPending}
            className={inputBase}
          />
          <button
            type="submit"
            disabled={create.isPending || name.trim().length === 0}
            className="h-11 shrink-0 whitespace-nowrap rounded-xl bg-[color:var(--team-primary)] px-5 font-medium text-[color:var(--team-on-primary)] disabled:pointer-events-none disabled:opacity-60 sm:w-auto"
          >
            {create.isPending ? 'Creating…' : 'Create group'}
          </button>
        </div>
        {error ? (
          <p className="text-xs text-[color:var(--color-danger)]">{error}</p>
        ) : null}
      </form>
      <UpgradeModal
        open={upgradeReason !== null}
        reason={upgradeReason}
        onClose={() => setUpgradeReason(null)}
      />
    </>
  );
}

function GroupRow({ group, me }: { group: GroupDto; me: UserPublic }) {
  const [expanded, setExpanded] = useState(false);
  const isOwner = me.id === group.ownerId;

  return (
    <li className="flex flex-col rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex min-h-11 items-center justify-between gap-3 px-4 py-3 text-left"
        aria-expanded={expanded}
      >
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-semibold">{group.name}</span>
          <span className="truncate text-xs text-[color:var(--color-fg-muted)]">
            {isOwner ? 'You own this group' : `Owner: ${group.ownerDisplayName}`}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          {/* Relative wrapper so the pending-requests dot can sit on the
              pill's top-right corner without pushing the chevron around. */}
          <span className="relative shrink-0">
            <span
              className="mono"
              style={{
                display: 'inline-block',
                fontSize: 10,
                background: 'var(--surface-2)',
                color: 'var(--fg-muted)',
                padding: '3px 10px',
                borderRadius: 999,
                letterSpacing: '0.08em',
                fontWeight: 700,
                whiteSpace: 'nowrap',
              }}
            >
              {/* Short count-only pill below sm; full "N members" text above
                  it, so a long owner-name + "N members" doesn't shove the
                  chevron off-screen on 320–374px devices. */}
              <span className="sm:hidden">{group.memberCount}</span>
              <span className="max-sm:hidden">
                {group.memberCount} member{group.memberCount === 1 ? '' : 's'}
              </span>
            </span>
            {group.pendingRequestCount > 0 ? (
              <span
                aria-label={`${group.pendingRequestCount} pending request${group.pendingRequestCount === 1 ? '' : 's'}`}
                style={{
                  position: 'absolute',
                  top: -2,
                  right: -2,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: 'var(--color-danger)',
                  border: '1.5px solid var(--bg)',
                  pointerEvents: 'none',
                }}
              />
            ) : null}
          </span>
          <span
            aria-hidden
            style={{
              fontSize: 12,
              color: 'var(--fg-muted)',
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.15s ease',
            }}
          >
            ▼
          </span>
        </div>
      </button>

      {expanded ? (
        <div className="flex flex-col gap-4 border-t border-[color:var(--color-border)] px-4 py-3">
          {/* Members list is visible to everyone in the group. Owners also get
              the remove-member affordance inside the list. */}
          <MembersList group={group} me={me} isOwner={isOwner} />
          {isOwner ? (
            <OwnerPanel group={group} />
          ) : (
            <MemberPanel group={group} me={me} />
          )}
        </div>
      ) : null}
    </li>
  );
}

function Avatar({
  displayName,
  avatarUrl,
  favoriteTeamFlagUrl,
}: {
  displayName: string;
  avatarUrl: string | null;
  favoriteTeamFlagUrl: string | null;
}) {
  const initial = (displayName.trim()[0] ?? '?').toUpperCase();
  // Prefer the user's favorite-team flag when set, otherwise fall back to
  // their uploaded avatar, and finally to an initials chip.
  const imgSrc = favoriteTeamFlagUrl ?? avatarUrl;
  if (imgSrc) {
    return (
      <img
        src={imgSrc}
        alt=""
        className="h-9 w-9 shrink-0 rounded-full object-cover"
        style={{ border: '1px solid var(--line)' }}
      />
    );
  }
  return (
    <div
      aria-hidden
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
      style={{
        background: 'var(--surface-2)',
        color: 'var(--fg-muted)',
        border: '1px solid var(--line)',
      }}
    >
      {initial}
    </div>
  );
}

function MembersList({
  group,
  me,
  isOwner,
}: {
  group: GroupDto;
  me: UserPublic;
  isOwner: boolean;
}) {
  const membersQuery = useGroupMembers(group.id, true);
  const kick = useKickMember();
  const [pendingKick, setPendingKick] = useState<GroupMemberDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const members = membersQuery.data?.members ?? [];

  async function onConfirmKick() {
    if (!pendingKick) return;
    setError(null);
    try {
      await kick.mutateAsync({ groupId: group.id, userId: pendingKick.userId });
      setPendingKick(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove member.');
      setPendingKick(null);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs uppercase tracking-wide text-[color:var(--color-fg-muted)]">
        Members
      </span>
      {membersQuery.isLoading ? (
        <Spinner size="sm" />
      ) : membersQuery.isError ? (
        <p className="text-xs text-[color:var(--color-danger)]">
          Couldn't load members.
        </p>
      ) : members.length === 0 ? (
        <p className="text-xs text-[color:var(--color-fg-muted)]">
          No members yet.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {members.map((m) => {
            const isSelf = m.userId === me.id;
            const showRemove = isOwner && !m.isOwner && !isSelf;
            return (
              <li
                key={m.userId}
                className="flex items-center gap-3 rounded-md bg-[color:var(--color-bg-elevated)] px-3 py-2"
              >
                <Avatar
                  displayName={m.displayName}
                  avatarUrl={m.avatarUrl}
                  favoriteTeamFlagUrl={m.favoriteTeamFlagUrl}
                />
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <span className="truncate text-sm font-medium">
                    {m.displayName}
                  </span>
                  <TierBadge tier={m.paymentTier} />
                  {m.isOwner ? (
                    <span
                      className="mono shrink-0"
                      style={{
                        fontSize: 10,
                        background: 'var(--surface-2)',
                        color: 'var(--fg-muted)',
                        padding: '2px 8px',
                        borderRadius: 999,
                        letterSpacing: '0.12em',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                      }}
                    >
                      Owner
                    </span>
                  ) : null}
                </div>
                {showRemove ? (
                  <button
                    type="button"
                    onClick={() => setPendingKick(m)}
                    disabled={kick.isPending}
                    className="h-9 min-w-[44px] shrink-0 rounded-md border border-[color:var(--color-border)] px-3 text-xs font-semibold text-[color:var(--color-fg-muted)] hover:text-[color:var(--color-danger)] disabled:pointer-events-none disabled:opacity-60"
                  >
                    Remove
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {error ? (
        <p className="text-xs text-[color:var(--color-danger)]">{error}</p>
      ) : null}

      {pendingKick ? (
        <ConfirmModal
          title={`Remove ${pendingKick.displayName}?`}
          body={
            <>
              They'll lose access to{' '}
              <b className="text-[color:var(--color-fg)]">{group.name}</b>.
            </>
          }
          confirmLabel="Remove"
          confirmVariant="danger"
          busy={kick.isPending}
          onConfirm={onConfirmKick}
          onCancel={() => setPendingKick(null)}
        />
      ) : null}
    </div>
  );
}

function OwnerPanel({ group }: { group: GroupDto }) {
  return (
    <div className="flex flex-col gap-4">
      <JoinRequestsList group={group} />
      <PendingInvitesList group={group} />
      <InviteLinkSection group={group} />
      <InviteForm group={group} />
      <DangerZone group={group} />
    </div>
  );
}

function InviteLinkSection({ group }: { group: GroupDto }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/join/${group.shortId}`;

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API unavailable (non-secure context / older browsers).
      // User can still select the text manually.
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs uppercase tracking-wide text-[color:var(--color-fg-muted)]">
        Invite link
      </span>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          type="text"
          value={url}
          readOnly
          onFocus={(e) => e.currentTarget.select()}
          className={`${inputBase} font-mono text-sm`}
        />
        <button
          type="button"
          onClick={onCopy}
          className="h-11 shrink-0 whitespace-nowrap rounded-xl bg-[color:var(--team-primary)] px-5 font-medium text-[color:var(--team-on-primary)] sm:w-auto"
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <p className="text-xs text-[color:var(--color-fg-muted)]">
        Anyone with the link can ask to join. You approve or reject each request.
      </p>
    </div>
  );
}

function humanizeInviteError(err: unknown): string {
  if (!(err instanceof ApiError)) return 'Could not send invite.';
  switch (err.code) {
    case 'ALREADY_MEMBER':
      return 'This email is already in the group.';
    case 'CANNOT_INVITE_SELF':
      return "You're already a member of this group.";
    case 'MAIL_NOT_CONFIGURED':
      return 'Email invitations are temporarily unavailable.';
    case 'INVITE_EMAIL_FAILED':
      return "Couldn't send the invitation.";
    default:
      return err.message || 'Could not send invite.';
  }
}

function InviteForm({ group }: { group: GroupDto }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [upgradeReason, setUpgradeReason] = useState<UpgradeReason | null>(null);
  const invite = useInviteByEmail(group.id);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSentTo(null);
    const trimmed = email.trim();
    if (!trimmed) return;
    try {
      await invite.mutateAsync(trimmed);
      setSentTo(trimmed);
      setEmail('');
    } catch (err) {
      const upgrade = upgradeReasonFromApiError(err);
      if (upgrade) {
        setUpgradeReason(upgrade);
        return;
      }
      setError(humanizeInviteError(err));
    }
  }

  return (
    <>
      <form onSubmit={onSubmit} className="flex flex-col gap-2">
        <span className="text-xs uppercase tracking-wide text-[color:var(--color-fg-muted)]">
          Invite by email
        </span>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(null);
              setSentTo(null);
            }}
            placeholder="friend@example.com"
            disabled={invite.isPending}
            className={inputBase}
          />
          <button
            type="submit"
            disabled={invite.isPending || email.trim().length === 0}
            className="h-11 shrink-0 whitespace-nowrap rounded-xl bg-[color:var(--team-primary)] px-5 font-medium text-[color:var(--team-on-primary)] disabled:pointer-events-none disabled:opacity-60 sm:w-auto"
          >
            {invite.isPending ? 'Sending…' : 'Invite'}
          </button>
        </div>
        {error ? (
          <p className="text-xs text-[color:var(--color-danger)]">{error}</p>
        ) : null}
        {sentTo ? (
          <p className="text-xs text-[color:var(--color-accent)]">
            Invite sent to {sentTo}.
          </p>
        ) : null}
      </form>
      <UpgradeModal
        open={upgradeReason !== null}
        reason={upgradeReason}
        onClose={() => setUpgradeReason(null)}
      />
    </>
  );
}

function relativeExpiry(expiresAt: string): string {
  const delta = new Date(expiresAt).getTime() - Date.now();
  if (delta <= 0) return 'expired';
  const minutes = Math.floor(delta / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days >= 1) return `in ${days} day${days === 1 ? '' : 's'}`;
  if (hours >= 1) return `in ${hours}h`;
  if (minutes >= 1) return `in ${minutes}m`;
  return 'in <1m';
}

function PendingInvitesList({ group }: { group: GroupDto }) {
  const invitesQuery = useGroupInvites(group.id, true);
  const revoke = useRevokeInvite();
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const invites = invitesQuery.data?.invites ?? [];

  // Same rule as JoinRequestsList: fully unmount when the list has loaded
  // successfully and is empty. Loading / error states keep the header so
  // the owner knows something went wrong.
  if (invitesQuery.isSuccess && invites.length === 0) return null;

  async function onRevoke(inviteId: string) {
    setRevokeError(null);
    try {
      await revoke.mutateAsync({ inviteId, groupId: group.id });
    } catch (err) {
      setRevokeError(
        err instanceof ApiError ? err.message : 'Could not revoke invite.',
      );
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs uppercase tracking-wide text-[color:var(--color-fg-muted)]">
        Pending invites
      </span>
      {invitesQuery.isLoading ? (
        <Spinner size="sm" />
      ) : invitesQuery.isError ? (
        <p className="text-xs text-[color:var(--color-danger)]">
          Couldn't load pending invites.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {invites.map((inv) => (
            <li
              key={inv.id}
              className="flex items-center justify-between gap-3 rounded-md bg-[color:var(--color-bg-elevated)] px-3 py-2 text-sm"
            >
              <div className="flex min-w-0 flex-col">
                <span className="truncate">{inv.email}</span>
                <span className="text-xs text-[color:var(--color-fg-muted)]">
                  expires {relativeExpiry(inv.expiresAt)}
                </span>
              </div>
              <button
                type="button"
                onClick={() => onRevoke(inv.id)}
                disabled={revoke.isPending}
                className="shrink-0 rounded-md border border-[color:var(--color-border)] px-3 text-xs font-semibold text-[color:var(--color-fg-muted)] hover:text-[color:var(--color-fg)] disabled:pointer-events-none disabled:opacity-60 max-md:h-10 max-md:px-4 md:py-1"
              >
                Revoke
              </button>
            </li>
          ))}
        </ul>
      )}
      {revokeError ? (
        <p className="text-xs text-[color:var(--color-danger)]">{revokeError}</p>
      ) : null}
    </div>
  );
}

function JoinRequestsList({ group }: { group: GroupDto }) {
  const requestsQuery = useJoinRequests(group.id, true);
  const approve = useApproveJoinRequest(group.id);
  const reject = useRejectJoinRequest(group.id);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  const [upgradeReason, setUpgradeReason] = useState<UpgradeReason | null>(null);

  const requests = requestsQuery.data?.requests ?? [];

  // When the list has loaded successfully and is empty, hide the whole
  // section — no header, no placeholder copy. Loading / error states still
  // render so the owner gets feedback.
  if (requestsQuery.isSuccess && requests.length === 0) return null;

  async function onApprove(id: string) {
    setError(null);
    setActingId(id);
    try {
      await approve.mutateAsync(id);
    } catch (err) {
      const upgrade = upgradeReasonFromApiError(err);
      if (upgrade) {
        setUpgradeReason(upgrade);
      } else {
        setError(
          err instanceof ApiError ? err.message : 'Could not approve request.',
        );
      }
    } finally {
      setActingId(null);
    }
  }

  async function onReject(id: string) {
    setError(null);
    setActingId(id);
    try {
      await reject.mutateAsync(id);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Could not reject request.',
      );
    } finally {
      setActingId(null);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs uppercase tracking-wide text-[color:var(--color-fg-muted)]">
        Pending requests
      </span>
      {requestsQuery.isLoading ? (
        <Spinner size="sm" />
      ) : requestsQuery.isError ? (
        <p className="text-xs text-[color:var(--color-danger)]">
          Couldn't load pending requests.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {requests.map((r) => {
            const busy = actingId === r.id;
            return (
              <li
                key={r.id}
                className="flex items-center gap-3 rounded-md bg-[color:var(--color-bg-elevated)] px-3 py-2"
              >
                <Avatar
                  displayName={r.displayName}
                  avatarUrl={r.avatarUrl}
                  favoriteTeamFlagUrl={r.favoriteTeamFlagUrl}
                />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="flex min-w-0 items-center gap-1.5 text-sm font-medium">
                    <span className="truncate">{r.displayName}</span>
                    <TierBadge tier={r.paymentTier} />
                  </span>
                  <span className="truncate text-xs text-[color:var(--color-fg-muted)]">
                    {r.email}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onApprove(r.id)}
                    disabled={busy}
                    aria-label={`Approve ${r.displayName}`}
                    className="flex h-11 w-11 items-center justify-center rounded-md border border-[color:var(--color-border)] text-lg font-semibold text-[color:var(--color-success)] hover:bg-[color:var(--color-success-soft)] disabled:pointer-events-none disabled:opacity-60"
                  >
                    ✓
                  </button>
                  <button
                    type="button"
                    onClick={() => onReject(r.id)}
                    disabled={busy}
                    aria-label={`Reject ${r.displayName}`}
                    className="flex h-11 w-11 items-center justify-center rounded-md border border-[color:var(--color-border)] text-lg font-semibold text-[color:var(--color-danger)] hover:bg-[color:var(--color-danger)]/10 disabled:pointer-events-none disabled:opacity-60"
                  >
                    ✗
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {error ? (
        <p className="text-xs text-[color:var(--color-danger)]">{error}</p>
      ) : null}
      <UpgradeModal
        open={upgradeReason !== null}
        reason={upgradeReason}
        onClose={() => setUpgradeReason(null)}
      />
    </div>
  );
}

function DangerZone({ group }: { group: GroupDto }) {
  const activeGroupId = useActiveGroup();
  const setActiveGroupId = useSetActiveGroup();
  const del = useDeleteGroup();
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  async function onConfirmDelete() {
    setError(null);
    try {
      await del.mutateAsync(group.id);
      if (activeGroupId === group.id) setActiveGroupId('global');
      setConfirming(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete group.');
      setConfirming(false);
    }
  }

  return (
    <div className="flex flex-col gap-2 border-t border-[color:var(--color-border)] pt-3">
      <span className="text-xs uppercase tracking-wide text-[color:var(--color-fg-muted)]">
        Danger zone
      </span>
      <button
        type="button"
        onClick={() => setConfirming(true)}
        disabled={del.isPending}
        className="h-11 rounded-xl border border-[color:var(--color-danger)] px-4 text-sm font-semibold text-[color:var(--color-danger)] hover:bg-[color:var(--color-danger)]/10 disabled:pointer-events-none disabled:opacity-60"
      >
        {del.isPending ? 'Deleting…' : 'Delete group'}
      </button>
      {error ? (
        <p className="text-xs text-[color:var(--color-danger)]">{error}</p>
      ) : null}

      {confirming ? (
        <ConfirmModal
          title={`Delete ${group.name}?`}
          body="This removes the group and kicks every member. This can't be undone."
          confirmLabel="Delete group"
          confirmVariant="danger"
          busy={del.isPending}
          onConfirm={onConfirmDelete}
          onCancel={() => setConfirming(false)}
        />
      ) : null}
    </div>
  );
}

function MemberPanel({ group, me }: { group: GroupDto; me: UserPublic }) {
  const activeGroupId = useActiveGroup();
  const setActiveGroupId = useSetActiveGroup();
  const leave = useLeaveGroup();
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  async function onConfirmLeave() {
    setError(null);
    try {
      await leave.mutateAsync({ groupId: group.id, userId: me.id });
      if (activeGroupId === group.id) setActiveGroupId('global');
      setConfirming(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not leave group.');
      setConfirming(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setConfirming(true)}
        disabled={leave.isPending}
        className="h-11 rounded-xl border border-[color:var(--color-border)] px-4 text-sm font-semibold hover:border-[color:var(--color-fg-muted)] disabled:pointer-events-none disabled:opacity-60"
      >
        {leave.isPending ? 'Leaving…' : 'Leave group'}
      </button>
      {error ? (
        <p className="text-xs text-[color:var(--color-danger)]">{error}</p>
      ) : null}

      {confirming ? (
        <ConfirmModal
          title={`Leave ${group.name}?`}
          body="You'll stop seeing this group's standings. The owner can re-invite you."
          confirmLabel="Leave"
          confirmVariant="danger"
          busy={leave.isPending}
          onConfirm={onConfirmLeave}
          onCancel={() => setConfirming(false)}
        />
      ) : null}
    </div>
  );
}
